import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import {
  Table,
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Tooltip,
} from "@mui/material";

import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import NotesIcon from "@mui/icons-material/Notes";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useNavigate } from "react-router-dom";
import { API_URL } from "./config";
import { formatDateTimeIST } from "./commonfunctions/dateUtils";

const ReportTimeOffRequest = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [logincampusCode, setCampusCode] = useState("");
  const [loginUsername, setUsername] = useState("");
  const [studentkey, setStudentKey] = useState("");
  const token = localStorage.getItem("auth");

  const [qrOpen, setQrOpen] = useState(false);
  const [qrToken, setQrToken] = useState("");
  const [qrRow, setQrRow] = useState(null);

  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
  };

  const scrollBoxSx = {
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
  };

  const tableSx = {
    minWidth: 1450,
    "& td, & th": {
      whiteSpace: "nowrap",
    },
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": {
      borderBottom: "1px solid rgba(25,118,210,0.10)",
      py: 0.9,
    },
  });

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    "&.Mui-disabled": {
      backgroundColor: "rgba(0,0,0,0.18)",
      color: "rgba(0,0,0,0.45)",
    },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconTextSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.7,
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      const campus = decoded.campusCode;
      const user = decoded.username;

      setCampusCode(campus);
      setUsername(user);
      setStudentKey(decoded.studentkey);

      axios
        .get(`${API_URL}/api/dataTimeOffRequest`, {
          params: {
            CampusCode: campus,
            StudentID: user,
          },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((resp) => {
          setData(resp.data || []);
        })
        .catch((err) => {
          console.error("Error fetching report:", err);
          if (err.response?.status === 401) navigate("/");
        });
    } catch (e) {
      console.error("Invalid token:", e);
      navigate("/");
    }
  }, [navigate, token]);

  const handleGenerateQR = async (row) => {
    try {
      const resp = await axios.post(
        `${API_URL}/api/timeoff/${row.TimeOffRequestID}/generate-qr`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setQrToken(resp.data.token);
      setQrRow(row);
      setQrOpen(true);
    } catch (err) {
      console.error("QR generation failed", err);
      alert(err.response?.data?.message || "Failed to generate QR");
    }
  };

  const statusChipSx = (status) => {
    const base = {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.2,
      py: 0.45,
      borderRadius: 1.5,
      fontWeight: 900,
      fontSize: "0.78rem",
      lineHeight: 1,
      border: "1px solid rgba(0,0,0,0.08)",
    };

    if (status === "Pending") {
      return {
        ...base,
        color: "#6a4b00",
        backgroundColor: "rgba(255,193,7,0.35)",
      };
    }

    if (status === "Rejected") {
      return {
        ...base,
        color: "white",
        backgroundColor: "rgba(244,67,54,0.85)",
        borderColor: "rgba(244,67,54,0.30)",
      };
    }

    if (status === "Approved") {
      return {
        ...base,
        color: "white",
        backgroundColor: "rgba(76,175,80,0.85)",
        borderColor: "rgba(76,175,80,0.30)",
      };
    }

    return {
      ...base,
      color: "rgba(25,118,210,0.95)",
      backgroundColor: "rgba(25,118,210,0.06)",
    };
  };

  const statusIcon = (status) => {
    if (status === "Approved") return <CheckCircleIcon sx={{ fontSize: 15 }} />;
    if (status === "Rejected") return <CancelIcon sx={{ fontSize: 15 }} />;
    if (status === "Pending") return <HourglassTopIcon sx={{ fontSize: 15 }} />;
    return <HourglassTopIcon sx={{ fontSize: 15 }} />;
  };

  const isApprovedEndDatePassed = (row) => {
    if (!row?.ApprovedLeaveEndDate) return false;
    return new Date() > new Date(row.ApprovedLeaveEndDate);
  };

  const areBothInOutScanned = (row) => {
    return Number(row?.OutScanCount || 0) > 0 && Number(row?.InScanCount || 0) > 0;
  };

  const getGenerateDisabledReason = (row) => {
    if (row.LeaveStatus !== "Approved") return "Not approved";
    if (isApprovedEndDatePassed(row)) return "Leave period ended";
    if (areBothInOutScanned(row)) return "IN and OUT already scanned";
    return "";
  };

  const canGenerateQR = (row) => {
    return row.LeaveStatus === "Approved" && !getGenerateDisabledReason(row);
  };

  const displayStatus = (item) => {
    if (item.LeaveStatus === "Pending" && item.CurrentApprovalStepName) {
      return `Approval Pending - ${item.CurrentApprovalStepName}`;
    }

    if (item.LeaveStatus === "Pending") {
      return "Approval Pending";
    }

    return item.LeaveStatus;
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <EventAvailableIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
                Time Off Request Report
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
                View your submitted leave requests and generate QR for approved leave
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={bodySx}>
          <Paper elevation={0} sx={tableWrapSx}>
            <Box sx={scrollBoxSx}>
              <TableContainer component="div">
                {data.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography
                      align="center"
                      sx={{
                        color: "rgba(25,118,210,0.80)",
                        fontWeight: 700,
                      }}
                    >
                      No records found
                    </Typography>
                  </Box>
                ) : (
                  <Table size="small" sx={tableSx}>
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell>#</TableCell>
                        <TableCell>Semester</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>
                          <Box sx={iconTextSx}>
                            <NotesIcon sx={{ fontSize: 17 }} />
                            Reason
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={iconTextSx}>
                            <CalendarMonthIcon sx={{ fontSize: 17 }} />
                            Approved Start
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={iconTextSx}>
                            <CalendarMonthIcon sx={{ fontSize: 17 }} />
                            Approved End
                          </Box>
                        </TableCell>
                        <TableCell>Days</TableCell>
                        <TableCell>Comments</TableCell>
                        <TableCell>
                          <Box sx={iconTextSx}>
                            <PersonIcon sx={{ fontSize: 17 }} />
                            Approved By
                          </Box>
                        </TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Approved Date</TableCell>
                        <TableCell>
                          <Box sx={iconTextSx}>
                            <QrCode2Icon sx={{ fontSize: 17 }} />
                            QR Code
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {data.map((item, idx) => (
                        <TableRow key={item.TimeOffRequestID} sx={zebraRowSx(idx)}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{item.Semester}</TableCell>
                          <TableCell>{item.LeaveType}</TableCell>

                          <TableCell
                            sx={{
                              maxWidth: 280,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <Tooltip title={item.ReasonForLeave || ""}>
                              <span>{item.ReasonForLeave || "-"}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{formatDateTimeIST(item.ApprovedLeaveStartDate)}</TableCell>
                          <TableCell>{formatDateTimeIST(item.ApprovedLeaveEndDate)}</TableCell>
                          <TableCell>{item.LeaveDays}</TableCell>

                          <TableCell
                            sx={{
                              maxWidth: 320,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <Tooltip title={item.Comments || ""}>
                              <span>{item.Comments || "-"}</span>
                            </Tooltip>
                          </TableCell>

                          <TableCell>{item.ApprovedBy || "-"}</TableCell>

                          <TableCell>
                            <Box component="span" sx={statusChipSx(item.LeaveStatus)}>
                              {statusIcon(item.LeaveStatus)}
                              {displayStatus(item)}
                            </Box>
                          </TableCell>

                          <TableCell>{formatDateTimeIST(item.ApprovedDate)}</TableCell>

                          <TableCell>
                            {item.LeaveStatus === "Approved" ? (
                              <Tooltip title={getGenerateDisabledReason(item) || "Generate QR"}>
                                <span>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    sx={primaryBtnSx}
                                    disabled={!canGenerateQR(item)}
                                    onClick={() => handleGenerateQR(item)}
                                    startIcon={<QrCode2Icon />}
                                  >
                                    Generate
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : (
                              <Typography sx={{ opacity: 0.75, fontWeight: 700 }}>
                                {displayStatus(item)}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            </Box>
          </Paper>

          <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle
              sx={{
                background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
                color: "white",
                fontWeight: 900,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <QrCode2Icon />
              Approved Leave QR
            </DialogTitle>

            <DialogContent sx={{ textAlign: "center", backgroundColor: BG }}>
              <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mt: 2 }}>
                {qrToken ? (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: "white",
                          border: `1px solid ${BORDER}`,
                        }}
                      >
                        <QRCodeCanvas value={qrToken} size={220} />
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />

                    <Typography sx={{ color: "rgba(25,118,210,0.90)", fontWeight: 700 }}>
                      Valid from <b>{formatDateTimeIST(qrRow?.ApprovedLeaveStartDate)}</b>
                      <br />
                      to <b>{formatDateTimeIST(qrRow?.ApprovedLeaveEndDate)}</b>
                    </Typography>

                    <Typography
                      sx={{
                        mt: 1,
                        color: "rgba(244,67,54,0.95)",
                        fontWeight: 900,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.7,
                      }}
                    >
                      <WarningAmberIcon sx={{ fontSize: 18 }} />
                      Do not share this QR
                    </Typography>

                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
                      <Button
                        variant="outlined"
                        sx={outlinedBtnSx}
                        onClick={() => setQrOpen(false)}
                        startIcon={<CloseIcon />}
                      >
                        Close
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography sx={{ p: 2, color: "rgba(25,118,210,0.80)", fontWeight: 700 }}>
                    No QR available
                  </Typography>
                )}
              </Paper>
            </DialogContent>
          </Dialog>
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportTimeOffRequest;